/**
 * LayerGroupSection Component
 *
 * Collapsible group header for organizing layers in the LayersPanel.
 * Uses Mantine Collapse + UnstyledButton with chevron rotation.
 */

import { memo, type ReactNode } from 'react';
import { Collapse, UnstyledButton, Badge } from '@mantine/core';
import { ChevronRight } from 'lucide-react';
import classes from './LayerGroupSection.module.css';

interface LayerGroupSectionProps {
  title: string;
  isCollapsed: boolean;
  onToggle: () => void;
  count: number;
  children: ReactNode;
  showHiddenIndicator?: boolean;
}

export const LayerGroupSection = memo(function LayerGroupSection({
  title,
  isCollapsed,
  onToggle,
  count,
  children,
  showHiddenIndicator = false,
}: LayerGroupSectionProps) {
  return (
    <div className={classes.group}>
      <UnstyledButton
        className={classes.header}
        onClick={onToggle}
        aria-expanded={!isCollapsed}
      >
        <ChevronRight
          size={16}
          className={`${classes.chevron} ${!isCollapsed ? classes.chevronOpen : ''}`}
        />
        <span className={classes.title}>{title}</span>
        {showHiddenIndicator && <span className={classes.hiddenDot} />}
        <Badge size="sm" variant="light" color="gray" className={classes.badge}>
          {count}
        </Badge>
      </UnstyledButton>

      <Collapse
        expanded={!isCollapsed}
        className={!isCollapsed ? classes.collapseOpen : undefined}
      >
        <div className={classes.content}>{children}</div>
      </Collapse>
    </div>
  );
});
